/**
 * Aging / Days Past Due Utilities
 *
 * CRITICAL: Always calculate aging from due_date and today's date at READ time.
 * Never use the stale days_past_due column from the database.
 */

import { differenceInDays } from 'date-fns'

export interface InvoiceForAging {
  due_date: string | Date
  status: string
  balance_due: number
}

/**
 * Calculate days past due for an invoice
 *
 * Returns:
 * - 0 for paid, written_off, or deleted_in_qbo invoices
 * - 0 for invoices with zero balance
 * - 0 for invoices not yet due (future due date)
 * - Positive number for overdue invoices (days since due date)
 */
export function calculateDaysPastDue(invoice: InvoiceForAging): number {
  // Paid, written off, or deleted invoices show 0
  if (invoice.status === 'paid' ||
      invoice.status === 'written_off' ||
      invoice.status === 'deleted_in_qbo') {
    return 0
  }

  // Zero balance shows 0
  if (invoice.balance_due <= 0) {
    return 0
  }

  const dueDate = new Date(invoice.due_date)
  const today = new Date()

  // Clear time components for accurate day calculation
  dueDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const daysPast = differenceInDays(today, dueDate)

  // Not yet due (future) shows 0
  if (daysPast < 0) {
    return 0
  }

  return daysPast
}

/**
 * Get aging bucket label for color coding
 *
 * Returns: '0-29', '30-59', '60-89', or '90+'
 */
export function getAgingBucket(daysPastDue: number): string {
  if (daysPastDue < 30) return '0-29'
  if (daysPastDue < 60) return '30-59'
  if (daysPastDue < 90) return '60-89'
  return '90+'
}

/**
 * Get aging color class for UI
 */
export function getAgingColorClass(daysPastDue: number): string {
  const bucket = getAgingBucket(daysPastDue)

  switch (bucket) {
    case '0-29':
      return 'text-yellow-600 bg-yellow-50'
    case '30-59':
      return 'text-orange-600 bg-orange-50'
    case '60-89':
      return 'text-red-600 bg-red-50'
    case '90+':
      return 'text-red-800 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-50'
  }
}

/**
 * Format aging for display
 *
 * Returns: "0 days", "15 days", "45 days", etc.
 */
export function formatAging(daysPastDue: number): string {
  return `${daysPastDue} day${daysPastDue === 1 ? '' : 's'}`
}
